import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, X, Save, Search, Printer, Upload, FileSpreadsheet, Paperclip, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductPlanning = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [generatingPO, setGeneratingPO] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    generic_name_strength: '',
    under_division: '',  // Renamed from brand_name
    packing: '',
    packaging_type: '',
    tablets_capsules: '',
    blister_color: '',
    back_foil_color: '',  // New field
    pill_color: '',
    mrp: '',
    pts: '',  // Price to Stockist
    qty: '',
    rate_to_us: '',
    value: '',
    gst: '',
    total_value: '',
    advance_given: '',
    balance_to_paid: '',  // New field (auto-calculated)
    design_file: '',  // For file upload (stores file_id)
    design_filename: '',  // For display purposes
    order_given_date: '',
    order_to: '',
    delivery_committed: ''
  });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/product-planning`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Failed to fetch products';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    
    // Auto-calculate Value = Qty × Rate to Us
    if (name === 'qty' || name === 'rate_to_us') {
      const qty = parseFloat(name === 'qty' ? value : formData.qty) || 0;
      const rate = parseFloat(name === 'rate_to_us' ? value : formData.rate_to_us) || 0;
      newFormData.value = (qty * rate).toFixed(2);
      
      // Auto-calculate Total Value = Value + (Value × GST/100)
      const gst = parseFloat(formData.gst) || 0;
      const calculatedValue = qty * rate;
      newFormData.total_value = (calculatedValue + (calculatedValue * gst / 100)).toFixed(2);
      
      // Auto-calculate Balance to Paid
      const advance = parseFloat(formData.advance_given) || 0;
      newFormData.balance_to_paid = ((calculatedValue + (calculatedValue * gst / 100)) - advance).toFixed(2);
    }
    
    // Auto-calculate Total Value when GST changes
    if (name === 'gst') {
      const calculatedValue = parseFloat(formData.value) || 0;
      const gst = parseFloat(value) || 0;
      newFormData.total_value = (calculatedValue + (calculatedValue * gst / 100)).toFixed(2);
      
      // Auto-calculate Balance to Paid
      const advance = parseFloat(formData.advance_given) || 0;
      newFormData.balance_to_paid = ((calculatedValue + (calculatedValue * gst / 100)) - advance).toFixed(2);
    }
    
    // Auto-calculate Balance to Paid when Advance Given changes
    if (name === 'advance_given') {
      const totalVal = parseFloat(formData.total_value) || 0;
      const advance = parseFloat(value) || 0;
      newFormData.balance_to_paid = (totalVal - advance).toFixed(2);
    }
    
    setFormData(newFormData);
  };

  const resetForm = () => {
    setFormData({
      product_name: '',
      generic_name_strength: '',
      under_division: '',
      packing: '',
      packaging_type: '',
      tablets_capsules: '',
      blister_color: '',
      back_foil_color: '',
      pill_color: '',
      mrp: '',
      pts: '',
      qty: '',
      rate_to_us: '',
      value: '',
      gst: '',
      total_value: '',
      advance_given: '',
      balance_to_paid: '',
      design_file: '',
      design_filename: '',
      order_given_date: '',
      order_to: '',
      delivery_committed: ''
    });
    setShowForm(false);
    setEditingId(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload only images (JPG, PNG, GIF) or PDF files');
      e.target.value = '';
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      e.target.value = '';
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/product-planning/upload-design`, formData, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store file_id and filename in form state
      setFormData(prev => ({
        ...prev,
        design_file: response.data.file_id,
        design_filename: response.data.filename
      }));
      
      toast.success(`File "${response.data.filename}" uploaded successfully`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    // Validate fileId
    if (!fileId || fileId === '' || fileId === '-') {
      toast.error('No file attached to this product');
      return;
    }
    
    // Use fetch instead of axios to avoid XMLHttpRequest blob responseType issues
    try {
      const response = await fetch(`${API}/product-planning/download-design/${fileId}`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${user.token}`,
          'Accept': 'application/octet-stream'
        }
      });
      
      if (!response.ok) {
        // Handle HTTP errors gracefully
        if (response.status === 400) {
          toast.error('Invalid file ID. This product has legacy data - file not available.');
        } else if (response.status === 404) {
          toast.error('File not found in storage.');
        } else if (response.status === 401) {
          toast.error('Authentication required. Please login again.');
        } else {
          toast.error('Unable to download file. Please try again.');
        }
        return;
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'design-file');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      // Comprehensive error handling for fetch API
      console.error('Download error (handled):', error);
      
      let errorMsg = 'Unable to download file. Please try again.';
      
      // Handle different types of fetch errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMsg = 'Network error. Please check your connection.';
      } else if (error.name === 'AbortError') {
        errorMsg = 'Download was cancelled.';
      }
      
      toast.error(errorMsg);
      
      // Ensure no error propagation
      return;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Helper function to safely convert to number
    const toNumber = (value) => {
      if (value === '' || value === null || value === undefined) return 0;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };
    
    // Convert empty strings and invalid values to 0 for numeric fields
    const sanitizedData = {
      ...formData,
      mrp: toNumber(formData.mrp),
      pts: toNumber(formData.pts),
      qty: toNumber(formData.qty),
      rate_to_us: toNumber(formData.rate_to_us),
      value: toNumber(formData.value),
      gst: toNumber(formData.gst),
      total_value: toNumber(formData.total_value),
      advance_given: toNumber(formData.advance_given),
      balance_to_paid: toNumber(formData.balance_to_paid)
    };
    
    try {
      if (editingId) {
        await axios.put(`${API}/product-planning/${editingId}`, sanitizedData, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/product-planning`, sanitizedData, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        toast.success('Product added successfully');
      }
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      // Handle Pydantic validation errors which come as an array
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save product';
      
      if (Array.isArray(errorDetail)) {
        // Pydantic validation error format
        errorMessage = errorDetail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      product_name: product.product_name,
      generic_name_strength: product.generic_name_strength || '',
      under_division: product.under_division || product.brand_name || '',  // Support old data
      packing: product.packing,
      packaging_type: product.packaging_type || '',
      tablets_capsules: product.tablets_capsules || '',
      blister_color: product.blister_color,
      back_foil_color: product.back_foil_color || '',
      pill_color: product.pill_color,
      mrp: product.mrp,
      pts: product.pts || '',
      qty: product.qty || '',
      rate_to_us: product.rate_to_us || '',
      value: product.value || '',
      gst: product.gst || '',
      total_value: product.total_value || '',
      advance_given: product.advance_given || '',
      balance_to_paid: product.balance_to_paid || '',
      design_file: product.design_file || product.design || '',
      order_given_date: product.order_given_date,
      order_to: product.order_to,
      delivery_committed: product.delivery_committed || ''
    });
    setEditingId(product.id);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/product-planning/${productId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Failed to delete product';
      toast.error(errorMessage);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const handleGeneratePO = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    setGeneratingPO(true);
    try {
      const response = await axios.post(
        `${API}/product-planning/generate-po`,
        selectedProducts,
        {
          headers: { 
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `PO_WINLIFE_${new Date().toISOString().split('T')[0]}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Purchase Order generated for ${selectedProducts.length} product(s)`);
      setSelectedProducts([]);
    } catch (error) {
      console.error('Error generating PO:', error);
      toast.error('Failed to generate Purchase Order');
    } finally {
      setGeneratingPO(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/template/download/product-planning`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Product_Planning_Upload_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download template');
    }
  };

  const handleBulkFileUpload = async (e, replace = false) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/product-planning/upload?replace=${replace}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
      fetchProducts();
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Failed to upload file';
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleUploadClick = () => {
    const replace = window.confirm(
      'Do you want to REPLACE all existing data?\n\n' +
      'Click OK to REPLACE (delete old data)\n' +
      'Click Cancel to ADD to existing data'
    );
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (e) => handleBulkFileUpload(e, replace);
    input.click();
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.product_name?.toLowerCase().includes(search) ||
      product.brand_name?.toLowerCase().includes(search) ||
      product.manufactured_by?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-slate-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead {
            display: table-header-group;
          }
          .overflow-x-auto {
            max-height: none !important;
            overflow: visible !important;
          }
          @page {
            size: landscape;
            margin: 0.5cm;
          }
        }
      `}</style>
      {/* Toolbar */}
      <div className="flex justify-between items-center no-print">
        <div className="flex items-center space-x-2">
          <Search className="w-5 h-5 text-slate-600" />
          <Input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button
            onClick={() => { fetchProducts(); toast.success('Data refreshed'); }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            size="sm"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button
            onClick={handleDownloadTemplate}
            variant="outline"
            size="sm"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Download Template
          </Button>
          <Button
            onClick={handleUploadClick}
            variant="outline"
            size="sm"
            disabled={uploading}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload Excel'}
          </Button>
          <Button
            onClick={handleGeneratePO}
            variant="outline"
            size="sm"
            disabled={generatingPO || selectedProducts.length === 0}
            className={selectedProducts.length > 0 ? 'bg-blue-50 border-blue-300' : ''}
          >
            <Printer className="w-4 h-4 mr-2" />
            {generatingPO ? 'Generating...' : `PO Print${selectedProducts.length > 0 ? ` (${selectedProducts.length})` : ''}`}
          </Button>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="add-product-button"
        >
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? 'Cancel' : 'Add New Product'}
        </Button>
      </div>

      {/* Product Form */}
      {showForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 no-print">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            {editingId ? 'Edit Product' : 'Add New Product'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Product Name *</label>
              <Input
                name="product_name"
                value={formData.product_name}
                onChange={handleInputChange}
                required
                placeholder="Enter product name"
                data-testid="product-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Generic Name & Strength</label>
              <Input
                name="generic_name_strength"
                value={formData.generic_name_strength}
                onChange={handleInputChange}
                placeholder="e.g., Paracetamol 500mg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Under Division</label>
              <Input
                name="under_division"
                value={formData.under_division}
                onChange={handleInputChange}
                placeholder="Enter division"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Packing</label>
              <Input
                name="packing"
                value={formData.packing}
                onChange={handleInputChange}
                placeholder="e.g., 10x10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Packaging Type</label>
              <Input
                name="packaging_type"
                value={formData.packaging_type}
                onChange={handleInputChange}
                placeholder="e.g., Blister"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tablets/Capsules</label>
              <Input
                name="tablets_capsules"
                value={formData.tablets_capsules}
                onChange={handleInputChange}
                placeholder="e.g., Tablets"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Blister Color</label>
              <Input
                name="blister_color"
                value={formData.blister_color}
                onChange={handleInputChange}
                placeholder="Enter color"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Back Foil Color</label>
              <Input
                name="back_foil_color"
                value={formData.back_foil_color}
                onChange={handleInputChange}
                placeholder="Enter color"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Pill Color</label>
              <Input
                name="pill_color"
                value={formData.pill_color}
                onChange={handleInputChange}
                placeholder="Enter color"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">MRP</label>
              <Input
                name="mrp"
                type="number"
                step="0.01"
                value={formData.mrp}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">PTS</label>
              <Input
                name="pts"
                type="number"
                step="0.01"
                value={formData.pts}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Qty</label>
              <Input
                name="qty"
                type="number"
                step="0.01"
                value={formData.qty}
                onChange={handleInputChange}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rate to Us</label>
              <Input
                name="rate_to_us"
                type="number"
                step="0.01"
                value={formData.rate_to_us}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Value (Auto)</label>
              <Input
                name="value"
                type="number"
                step="0.01"
                value={formData.value}
                readOnly
                className="bg-slate-100"
                placeholder="Auto-calculated"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">GST (%)</label>
              <Input
                name="gst"
                type="number"
                step="0.01"
                value={formData.gst}
                onChange={handleInputChange}
                placeholder="18"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Total Value (Auto)</label>
              <Input
                name="total_value"
                type="number"
                step="0.01"
                value={formData.total_value}
                readOnly
                className="bg-slate-100"
                placeholder="Auto-calculated"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Design</label>
              <Input
                name="design"
                value={formData.design}
                onChange={handleInputChange}
                placeholder="Design details"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Order Given Date</label>
              <Input
                name="order_given_date"
                type="date"
                value={formData.order_given_date}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Order To</label>
              <Input
                name="order_to"
                value={formData.order_to}
                onChange={handleInputChange}
                placeholder="Supplier name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Advance Given</label>
              <Input
                name="advance_given"
                type="number"
                step="0.01"
                value={formData.advance_given}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Balance to Paid (Auto)</label>
              <Input
                name="balance_to_paid"
                type="number"
                step="0.01"
                value={formData.balance_to_paid}
                readOnly
                className="bg-slate-100"
                placeholder="Auto-calculated"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Design File (Image/PDF)</label>
              <Input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileUpload}
                disabled={uploading}
              />
              {uploading && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
              {formData.design_file && !uploading && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Uploaded: {formData.design_filename || 'File attached'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Delivery Committed</label>
              <Input
                name="delivery_committed"
                type="date"
                value={formData.delivery_committed}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-span-4 flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="save-product-button">
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Update' : 'Save'} Product
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Products Table */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-20">
          <Plus className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">No products in planning</p>
          <p className="text-slate-500 text-sm mt-2">Click "Add New Product" to start planning</p>
        </div>
      ) : (
        <div className="print-content">
          <div className="hidden print:block p-4 border-b">
            <h1 className="text-xl font-bold">Product Planning Report</h1>
            <p className="text-sm text-gray-600">Total Products: {filteredProducts.length}</p>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: '60vh' }}>
            <table className="w-full" data-testid="planning-table">
              <thead className="sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-200 shadow-sm print:static print:bg-white">
              <tr>
                <th className="px-2 py-2 text-center text-xs font-semibold text-slate-700 uppercase no-print" style={{ fontSize: '10px' }}>
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 cursor-pointer"
                  />
                </th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Product Name</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Generic & Strength</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Division</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Packing</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Packaging Type</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Tablets/Caps</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Blister</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Pill</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>MRP</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>PTS</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Qty</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Rate</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Value</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>GST%</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Total Value</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Design</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Order Date</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Order To</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Advance</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase" style={{ fontSize: '10px' }}>Delivery</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700 uppercase no-print" style={{ fontSize: '10px' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredProducts.map((product, index) => (
                <tr key={product.id} className="hover:bg-slate-50 transition-colors" data-testid={`product-row-${index}`}>
                  <td className="px-2 py-2 text-center no-print">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={() => handleSelectProduct(product.id)}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-800 font-medium">{product.product_name}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.generic_name_strength || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.under_division || product.brand_name || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.packing || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.packaging_type || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.tablets_capsules || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.blister_color || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.pill_color || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.mrp || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.pts || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.qty || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.rate_to_us || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700 font-medium">{product.value || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.gst || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700 font-semibold">{product.total_value || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">
                    {(product.design_file || product.design) ? (
                      <div className="flex items-center justify-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            
                            // Use Promise.resolve to ensure async errors are caught
                            Promise.resolve(handleDownloadFile(product.design_file || product.design, product.product_name + '-design'))
                              .catch((err) => {
                                console.error('Download error (button handler):', err);
                                toast.error('Unable to download file');
                              });
                          }}
                          className="hover:bg-blue-50 p-1 rounded transition-colors"
                          title={product.design_file ? "Click to download design file" : "Legacy data - file may not be available"}
                        >
                          <Paperclip className={`w-4 h-4 ${product.design_file ? 'text-blue-600' : 'text-gray-400'}`} />
                        </button>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.order_given_date || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.order_to || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.advance_given || '-'}</td>
                  <td className="px-2 py-2 text-xs text-slate-700">{product.delivery_committed || '-'}</td>
                  <td className="px-2 py-2 no-print">
                    <div className="flex space-x-1">
                      <Button
                        onClick={() => handleEdit(product)}
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        data-testid={`edit-product-${index}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(product.id)}
                        size="sm"
                        variant="destructive"
                        className="h-7 px-2"
                        data-testid={`delete-product-${index}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}
    </div>
  );
};

export default ProductPlanning;
